/**
 * PostMessage Protocol Specification
 *
 * Defines the communication protocol between the parent app and embedded
 * minting dapps (external iframes). This file exports message types and
 * helper functions for encoding/decoding messages.
 *
 * All messages follow this structure:
 * ```typescript
 * {
 *   type: MessageType.SOME_TYPE,
 *   payload: { ... },
 *   sender: 'parent' | 'child',
 *   timestamp: number
 * }
 * ```
 *
 * @module minting/protocol
 * @category Minting Framework
 */

export * from './types';
// `export *` re-exports the names but does NOT bind them into this module's
// local scope, so the MessageType.X references below resolved to an undefined
// global. It is a runtime enum, so it needs an explicit value import.
import { MessageType } from './types';

/**
 * Utility to create a properly formatted message
 */
export function createMessage(
  type: string,
  payload: any,
  sender: 'parent' | 'child'
): PostMessageEvent {
  return {
    type,
    payload,
    sender,
    timestamp: Date.now(),
  };
}

/**
 * Utility to create parent-to-child AppKit init message
 */
export function createAppKitInitMessage(walletState: WalletState): PostMessageEvent {
  return createMessage(MessageType.APPKIT_INIT, walletState, 'parent');
}

/**
 * Utility to create parent-to-child AppKit update message
 */
export function createAppKitUpdateMessage(walletState: WalletState): PostMessageEvent {
  return createMessage(MessageType.APPKIT_UPDATE, walletState, 'parent');
}

/**
 * Utility to create transaction response
 */
export function createTransactionResponse(
  requestId: string,
  success: boolean,
  result?: any,
  error?: string
): TransactionResponse {
  return {
    requestId,
    success,
    result,
    error,
  };
}

/**
 * Utility to create error message
 */
export function createErrorMessage(
  error: string,
  requestId?: string,
  code?: string | number
): ErrorMessage {
  return {
    type: MessageType.ERROR,
    payload: {
      requestId,
      error,
      code,
    },
  };
}

/**
 * Send a message to an iframe
 */
export async function sendToIframe(
  iframe: HTMLIFrameElement,
  message: PostMessageEvent,
  targetOrigin: string = '*'
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      iframe.contentWindow?.postMessage(message, targetOrigin);
      resolve();
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Send a message to the parent window (from iframe)
 */
export async function sendToParent(message: PostMessageEvent): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      window.parent.postMessage(message, '*'); // Use specific origin in production
      resolve();
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Request parent to sign a transaction (child -> parent)
 */
export async function requestSignTransaction(
  transaction: any,
  requestId: string,
  targetOrigin: string = '*'
): Promise<void> {
  const message: RequestSignTxMessage = {
    type: MessageType.REQUEST_SIGN_TX,
    payload: {
      transaction,
      requestId,
      chainId: window.ethereum?.chainId || null,
    },
    sender: 'child',
    timestamp: Date.now(),
  };
  await sendToParent(message);
}

/**
 * Request parent to sign a message (child -> parent)
 */
export async function requestSignMessage(
  messagePayload: string | { types?: any; primaryType?: string; domain?: any; message?: any },
  requestId: string,
  address?: string
): Promise<void> {
  const message: RequestSignMessageMessage = {
    type: MessageType.REQUEST_SIGN_MESSAGE,
    payload: {
      message: messagePayload,
      requestId,
      address,
    },
    sender: 'child',
    timestamp: Date.now(),
  };
  await sendToParent(message);
}

/**
 * Request parent to switch chain (child -> parent)
 */
export async function requestSwitchChain(chainId: number, requestId: string): Promise<void> {
  const message: RequestSwitchChainMessage = {
    type: MessageType.REQUEST_SWITCH_CHAIN,
    payload: {
      chainId,
      requestId,
    },
    sender: 'child',
    timestamp: Date.now(),
  };
  await sendToParent(message);
}

/**
 * Notify parent that child iframe is ready
 */
export async function notifyChildReady(): Promise<void> {
  const message = createMessage(MessageType.CHILD_READY, {}, 'child');
  await sendToParent(message);
}

/**
 * Notify parent of an analytics/event log
 */
export async function logEvent(event: string, data?: any): Promise<void> {
  const message = createMessage(MessageType.EVENT_LOG, { event, data }, 'child');
  await sendToParent(message);
}